import React, { useEffect, useRef, useState } from 'react';
import { Form, Button, Typography } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from "react-i18next";
import { validateEmail, validatePhone, validateTransactionPin, validateConfirmTransactionPin, stringIsNullOrEmpty } from '../../common/util';
import MemberService from '../../services/member_service';
import { ApiKey, HttpStatusCode, Page, SessionKey } from '../../common/constant';
import { useNavigate } from 'react-router-dom';

/// <summary>
/// Author: SY
/// </summary>
const Register = () => {
    const [loading, setLoading] = useState(false);
    const { control, handleSubmit, watch, setValue } = useForm();
    const { t } = useTranslation();
    const _DEFAULT_SECONDS_TIMEOUT = '59';
    const _OTP_EXPECTED_LENGTH = 6;
    const [seconds, setSeconds] = useState('');
    const [hasOtpSent, setHasOtpSent] = useState(false);
    const [sentEmailValue, setSentEmailValue] = useState('');

    const _memberService = new MemberService();
    const [submitted, setSubmitted] = useState(false);
    const _navigate = useNavigate();

    const emailValue = watch('email');
    var _timeoutRef = useRef(null);

    /// <summary>
    /// Author: SY
    /// </summary>
    useEffect(() => {
        setSeconds('');
        setSubmitted(false);

        if (_timeoutRef.current != null) {
            clearTimeout(_timeoutRef.current);

            _timeoutRef.current = null;
        }
    }, []);

    /// <summary>
    /// Author: SY
    /// </summary>
    useEffect(() => {
        if (!stringIsNullOrEmpty(sessionStorage.getItem(SessionKey._EMAIL)) && !stringIsNullOrEmpty(seconds)) {
            _timeoutRef.current = setTimeout(() => {
                var convertedInt = parseInt(seconds);

                if (convertedInt >= 0) {
                    setSeconds((convertedInt - 1).toString().padStart(2, '0'));
                }
            }, 1000);
        }
    }, [sessionStorage.getItem(SessionKey._EMAIL), seconds]);

    /// <summary>
    /// Author: SY
    /// </summary>
    useEffect(() => {
        const emailFromSession = sessionStorage.getItem(SessionKey._EMAIL);
        if (emailFromSession) {
            setValue('email', emailFromSession);
        }
    }, []);

    /// <summary>
    /// Author: SY
    /// </summary>
    const onSubmit = async (data) => {
        setSubmitted(true);
        setLoading(true);

        try {
            const payload = {
                fullname: data.fullname,
                transactionPin: data.transactionPin,
                confirmTransactionPin: data.confirmTransactionPin,
                email: data.email,
                phoneNumber: !stringIsNullOrEmpty(data?.phoneNumber) ? data.phoneNumber : '',
                otp: data.otp,
                addressOne: data.addressOne,
                addressTwo: data.addressTwo,
                postalCode: data.postalCode,
                city: data.city,
                state: data.state,
                country: data.country
            };
            const response = await _memberService.register(payload);

            if (response[ApiKey._SUCCESS_KEY]) {
                sessionStorage.removeItem(SessionKey._EMAIL);

                if (response[ApiKey._DATA_KEY]['verified']) {
                    sessionStorage.removeItem(SessionKey._XCHANGE_KEY);

                    window.location.replace(response[ApiKey._DATA_KEY]['callback']['content']);
                }
                else {
                    _navigate(`${Page._BIOMETRIC_VERIFICATION_MAIN_URL}?key=${response[ApiKey._DATA_KEY]['key']}`, { replace: true });
                }
            }
            else {
                // alert(response[ApiKey._MESSAGE_KEY]);
                setSubmitted(false);
            }

        } catch (error) {
            _navigate(Page._VENDOR_ACC_AUTHENTICATE_EXPIRED_URL, { replace: true });
        } finally {
            setLoading(false);
            setSubmitted(false);
        }
    };

    /// <summary>
    /// Author: SY
    /// </summary>
    async function sendOtp(email) {
        try {
            var response = await _memberService.requestAuthEmailOtp(email);

            setSeconds((response.data ? response.data : _DEFAULT_SECONDS_TIMEOUT).toString().padStart(2, '0'));
        }
        catch (error) {
            _navigate(Page._VENDOR_ACC_AUTHENTICATE_EXPIRED_URL, { replace: true });
        }
    }

    return (
        <div className='auth-container' style={{
            paddingTop: '10%',
            paddingBottom: '10%',
            height: 'auto'
        }}>
            <div className='auth-data-wrapper' style={{
                maxHeight: 'none',
                height: 'auto',
            }}>
                <div style={{ padding: '10px 0 10px 20px', borderBottom: '1px solid rgb(201, 201, 201)' }}>
                    <Typography.Text style={{ fontSize: 'small' }}>{t("REGISTER")}</Typography.Text>
                </div>
                <i className="fas fa-arrow-left back" onClick={() => {
                    const key = sessionStorage.getItem(SessionKey._XCHANGE_KEY);
                    _navigate(`${Page._VENDOR_ACC_EMAIL_LOGIN_URL}?key=${key}`);
                }} />
                <div className='auth-data-title'>
                    <Typography.Title level={3}>{t("REGISTER")}</Typography.Title>
                    <Typography.Text>{t("SET_UP_YOUR_ACCOUNT_IN_JUST_A_FEW_STEPS")}</Typography.Text>
                </div>

                <div className='auth-data-input-wrapper' style={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '10px 0',
                    gap: '10px'
                }}>
                    <Form layout="vertical" onFinish={handleSubmit(onSubmit)} style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <Controller
                            name="fullname"
                            control={control}
                            rules={{ required: t("FULLNAME_REQUIRED") }}
                            render={({ field, fieldState }) => (
                                <div className='input-container' >
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input

                                            type="text"
                                            name={field.name}
                                            required
                                            defaultValue={field.value}
                                            placeholder=" "
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='username'
                                        />
                                        <label htmlFor='fullname' className='placeholder'>{t("FULLNAME")}</label>
                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )}
                        />

                        <Controller
                            name="transactionPin"
                            control={control}
                            rules={{ validate: validateTransactionPin }}
                            render={({ fieldState, field }) => {
                                return (
                                    <div className='input-container'>
                                        <div className="input-wrapper" style={{ width: '100%' }}>
                                            <input type="password" name={field.name} required pattern="[0-9]*" inputMode="numeric" defaultValue={field.value} placeholder=" " onInput={(e) => {
                                                e.target.value = e.target.value.replace(/[^0-9]/g, '').substring(0, 6);

                                                if (e.target.value.length <= 6) {
                                                    field.onChange(e.target.value);
                                                }
                                            }} id='transactionPin' />
                                            <label htmlFor='transactionPin' className='placeholder'>{t("TRANSACTION_PIN")}</label>
                                        </div>
                                        {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                    </div>
                                )
                            }}
                        />

                        <Controller
                            name="confirmTransactionPin"
                            control={control}
                            rules={{
                                validate: (value) => validateConfirmTransactionPin(value, control._formValues)
                            }}
                            render={({ field, fieldState }) => (
                                <div className='input-container'  >
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="password"
                                            inputMode='numeric'
                                            name={field.name}
                                            required
                                            defaultValue={field.value}
                                            placeholder=" "
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='confirmTransactionPin'
                                        />
                                        <label htmlFor='confirmTransactionPin' className='placeholder'>{t("CONFIRM_TRANSACTION_PIN")}</label>
                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )}
                        />

                        <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{ validate: validatePhone }}
                            defaultValue={''}
                            render={({ field, fieldState }) => (
                                <div className='input-container'  >
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="number"
                                            name={field.name}
                                            required
                                            defaultValue={field.value}
                                            placeholder=" "
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='phoneNumber'
                                        />
                                        <label htmlFor='phoneNumber' className='placeholder'>{t("PHONE_NUMBER")}</label>

                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )}
                        />

                        <Controller
                            control={control}
                            name={'addressOne'}
                            rules={{ validate: (value) => !stringIsNullOrEmpty(value) || t('ADDRESS_1_REQUIRED') }}
                            defaultValue={''}
                            render={({ field, fieldState }) => (
                                <div className='input-container'  >
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="text"
                                            name={field.name}
                                            required
                                            defaultValue={field.value}
                                            placeholder=" "
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='addressOne'
                                        />
                                        <label htmlFor='addressOne' className='placeholder'>{t("ADDRESS_1")}</label>

                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )} />

                        <Controller
                            control={control}
                            name={'addressTwo'}
                            defaultValue={''}
                            render={({ field, fieldState }) => (
                                <div className='input-container'  >
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="text"
                                            name={field.name}
                                            required
                                            defaultValue={field.value}
                                            placeholder=" "
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='addressTwo'
                                        />
                                        <label htmlFor='addressTwo' className='placeholder'>{t("ADDRESS_2")}</label>

                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )} />

                        <Controller
                            control={control}
                            name={'postalCode'}
                            rules={{
                                validate: {
                                    required: (value) => !stringIsNullOrEmpty(value) || t('POSTAL_CODE_REQUIRED'),
                                }
                            }}
                            defaultValue={''}
                            render={({ field, fieldState }) => (
                                <div className='input-container'>
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="text"
                                            name={field.name}
                                            required
                                            placeholder=" "
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='postalCode'
                                        />
                                        <label htmlFor='postalCode' className='placeholder'>{t("POSTAL_CODE")}</label>
                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )}
                        />

                        <Controller
                            control={control}
                            name={'city'}
                            rules={{ validate: (value) => !stringIsNullOrEmpty(value) || t('CITY_REQUIRED') }}
                            defaultValue={''}
                            render={({ field, fieldState }) => (
                                <div className='input-container'>
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="text"
                                            name={field.name}
                                            required
                                            placeholder=" "
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='city'
                                        />
                                        <label htmlFor='city' className='placeholder'>{t("CITY")}</label>
                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )}
                        />

                        <Controller
                            control={control}
                            name={'state'}
                            rules={{
                                validate: (value) => !stringIsNullOrEmpty(value) || t('STATE_REQUIRED')
                            }}
                            defaultValue={''}
                            render={({ field, fieldState }) => (
                                <div className='input-container'>
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="text"
                                            name={field.name}
                                            required
                                            placeholder=" "
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='state'
                                        />
                                        <label htmlFor='state' className='placeholder'>{t("STATE")}</label>
                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )}
                        />

                        <Controller
                            control={control}
                            name={'country'}
                            rules={{
                                validate: (value) => !stringIsNullOrEmpty(value) || t('COUNTRY_REQUIRED')
                            }}
                            defaultValue={''}
                            render={({ field, fieldState }) => (
                                <div className='input-container'>
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="text"
                                            name={field.name}
                                            required
                                            placeholder=" "
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            id='country'
                                        />
                                        <label htmlFor='country' className='placeholder'>{t("COUNTRY")}</label>
                                    </div>
                                    {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                </div>
                            )}
                        />

                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <div className='input-container'  >
                                    <div className="input-wrapper" style={{ width: '100%' }}>
                                        <input
                                            type="email"
                                            name={field.name}
                                            defaultValue={field.value}
                                            placeholder=" "
                                            id='email'
                                            disabled={true}
                                            style={{ backgroundColor: '#f5f5f5' }}
                                        />
                                    </div>
                                    <Button
                                        type="primary"
                                        className="submit-btn"
                                        disabled={
                                            validateEmail(emailValue) !== true ||
                                            submitted ||
                                            (!stringIsNullOrEmpty(seconds) && parseInt(seconds) >= 0)
                                        }
                                        onClick={async () => {
                                            setSeconds('');
                                            await sendOtp(emailValue);
                                            setHasOtpSent(true);
                                            setSentEmailValue(emailValue);
                                        }}
                                        style={{
                                            marginTop: '3px',
                                            width: '100%',
                                            textAlign: 'center',
                                            border: 'none',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                        }}
                                    >
                                        {(hasOtpSent ? t("SEND") : t("SEND_OTP")) +
                                            (!stringIsNullOrEmpty(seconds) && parseInt(seconds) >= 0 ? ` (${seconds})` : '')}
                                    </Button>
                                </div>
                            )}
                        />

                        <Controller
                            control={control}
                            rules={{ required: 'Please verify with valid OTP', minLength: { value: _OTP_EXPECTED_LENGTH, message: 'Please verify with valid OTP' } }}
                            name={'otp'}
                            defaultValue={''}
                            render={({ fieldState, field }) => {
                                return (
                                    <div className='input-container'>
                                        {hasOtpSent && sentEmailValue && (
                                            <div style={{ marginTop: '5px', fontSize: '12px' }}>
                                                {t("EMAIL_SENT_TO")}: {sentEmailValue}
                                            </div>
                                        )}
                                        <div className="input-wrapper" style={{ width: '100%' }}>
                                            <input type="text" name={field.name} required pattern="[0-9]*" inputMode="numeric" defaultValue={field.value} placeholder=" " onInput={(e) => {
                                                e.target.value = e.target.value.replace(/[^0-9]/g, '').substring(0, 6);

                                                if (e.target.value.length <= 6) {
                                                    field.onChange(e.target.value);
                                                }
                                            }} id='input' />
                                            <label htmlFor='input' className='placeholder'>{t("ONE_TIME_PASSWORD")}</label>
                                        </div>
                                        {fieldState.error && <span className='form-error'>{fieldState.error.message}</span>}
                                    </div>
                                )
                            }} />

                    </Form>
                </div>

                <div className='auth-data-submit-wrapper' style={{
                    marginTop: '10px',
                    position: 'relative',
                }}>
                    <Button type="primary" onClick={() => handleSubmit(onSubmit)()}>
                        {t("SUBMIT")}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Register;